import { Box, Grid } from '@mui/material';
import { FunctionComponent, useEffect } from 'react';
import { Navbar, Label, SearchInputWrapper, Switch } from 'src/shared';
import { useSearchParams } from 'react-router-dom';
import {
    Root,
    SectionHeader,
    SectionHeaderLabels,
    S,
    AddRowChip,
    StyledAddCommentIcon
} from './styled';
import Header from './components/ClientHeader';
import WorkTime from './components/WorkTime';
import Requests from './components/Requests';
import { ClientSelectors } from 'src/redux/client/selectors';
import {
    fetchProjectWorkTimeReview,
    fetchProjectRepositories
} from 'src/redux/client/service';
import { getProjectDetails } from 'src/redux/projects/service';
import useAppDispatch from 'src/hooks/useAppDispatch';
import { useNavigate } from 'react-router-dom';
import {
    setQuestsSearchQuery,
    setQuestsStatusFilter,
    setOnlyCurrentWork
} from 'src/redux/client/slice';
import { CURRENT_WORK_QUEST_STATUS } from 'src/constants/quest';
import { setSnackbarAttributes } from 'src/redux/snackbar/slice';
import { selectProject } from 'src/redux/client/service';

type Props = Record<string, never>;

const ClientDashboard: FunctionComponent<Props> = ({}: Props) => {
    const dispatch = useAppDispatch();
    const { workTimeReview, selectedProjectId, questsSearchQuery } =
        ClientSelectors();

    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    useEffect(() => {
        const checkoutStatus = searchParams.get('checkout_setup');
        if (!checkoutStatus) {
            return;
        }

        const projectIdFromUrl = searchParams.get('project_id');
        if (projectIdFromUrl) {
            dispatch(selectProject(projectIdFromUrl));
        }

        if (checkoutStatus === 'success') {
            dispatch(
                setSnackbarAttributes({
                    snackbarOpen: true,
                    snackbarType: 'success',
                    snackbarMessage: 'Payment method saved.'
                })
            );
        } else if (checkoutStatus === 'cancelled') {
            dispatch(
                setSnackbarAttributes({
                    snackbarOpen: true,
                    snackbarType: 'info',
                    snackbarMessage: 'Payment setup cancelled.'
                })
            );
        }

        const next = new URLSearchParams(searchParams);
        next.delete('checkout_setup');
        next.delete('project_id');
        setSearchParams(next, { replace: true });
    }, [dispatch, searchParams, setSearchParams]);

    useEffect(() => {
        if (selectedProjectId) {
            dispatch(fetchProjectWorkTimeReview());
            dispatch(fetchProjectRepositories(selectedProjectId));
            dispatch(getProjectDetails(selectedProjectId));
        }
    }, [dispatch, selectedProjectId]);

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        dispatch(setQuestsSearchQuery(event.target.value));
    };

    const handleSearchClear = () => {
        dispatch(setQuestsSearchQuery(''));
    };

    const handleSwitchToggle = (isChecked: boolean) => {
        dispatch(setOnlyCurrentWork(isChecked));
        if (isChecked) {
            dispatch(setQuestsStatusFilter(CURRENT_WORK_QUEST_STATUS));
        } else {
            dispatch(setQuestsStatusFilter([]));
        }
    };

    return (
        <Navbar>
            <Root sx={S.rootContainer}>
                <Header />
                <Grid
                    container
                    spacing={2}
                    columns={{ xs: 12, sm: 12, md: 12, lg: 12 }}
                >
                    <Grid item xs={12} sm={12} md={7} lg={7}>
                        <Box sx={S.requestsHeaderBoxSx}>
                            <Box>
                                <Label sxOverrides={S.sectionTitle}>
                                    Requests{' '}
                                </Label>
                            </Box>
                            <Box sx={S.responsiveSectionSubtitleBoxSx}>
                                <Label
                                    sxOverrides={S.responsiveSectionSubtitle}
                                >
                                    The following is the history of all the
                                    features that Beehive is developing
                                </Label>
                            </Box>
                            <Box sx={S.mobileSwitchBoxSx}>
                                <Switch
                                    onchange={handleSwitchToggle}
                                    label="Only current work"
                                    variant="simple"
                                    checked={true}
                                />
                            </Box>
                        </Box>
                    </Grid>

                    <Grid item xs={12} sm={12} md={5} lg={5}>
                        <Box sx={S.actionsContainerSx}>
                            <Box sx={S.desktopSwitchBoxSx}>
                                <Switch
                                    onchange={handleSwitchToggle}
                                    label="Only current work"
                                    variant="simple"
                                    checked={true}
                                />
                            </Box>

                            <Box sx={S.searchContainerSx}>
                                <Box sx={S.searchInputBoxSx}>
                                    <SearchInputWrapper
                                        search={questsSearchQuery}
                                        handleSearch={handleSearchChange}
                                        onClear={handleSearchClear}
                                        sx={S.searchInputStyle}
                                    />
                                </Box>

                                <Box sx={S.addButtonBoxSx}>
                                    <AddRowChip
                                        label="New request"
                                        deleteIcon={<StyledAddCommentIcon />}
                                        clickable
                                        onClick={() => navigate('/plan/quest')}
                                        onDelete={() => navigate('/plan/quest')}
                                        sx={S.newRequestChip}
                                    />
                                </Box>
                            </Box>
                        </Box>
                    </Grid>
                </Grid>

                <Requests />

                <SectionHeader>
                    <SectionHeaderLabels>
                        <Label sxOverrides={S.sectionTitle}>
                            Total net work time for each month
                        </Label>
                        <Label sxOverrides={S.sectionSubtitle}>
                            This graph summarizes the total net work time for
                            each month for the last 6 months.
                        </Label>
                    </SectionHeaderLabels>
                </SectionHeader>
                <Box>
                    <WorkTime data={workTimeReview} />
                </Box>
            </Root>
        </Navbar>
    );
};

export default ClientDashboard;
